import type { LocalProcedure, ProcedureInput } from '@iculogbook/shared';
import { uuidv7 } from 'uuidv7';

import { db } from '../db.ts';
import { ensurePersistentStorage } from '../persist.ts';
import {
  byOccurredAtDescending,
  isLive,
  notifyLocalWrite,
  outboxItem,
  userRange,
  writeWithOutbox,
} from './internal.ts';

/** How many recently-used procedures the picker pins above the divider. */
const RECENT_PROCEDURE_COUNT = 5;

export const proceduresRepository = {
  /**
   * Writes a new procedure and returns immediately. This is the path behind the
   * PRD's ten-second target, so nothing slow may be added to it.
   */
  async create(userId: string, input: ProcedureInput): Promise<LocalProcedure> {
    const now = new Date();

    const record: LocalProcedure = {
      ...input,
      id: uuidv7(),
      userId,
      source: 'manual',
      createdAt: now,
      updatedAt: now,
      syncedAt: null,
    };

    await writeWithOutbox('procedure', record.id, userId, async () => {
      await db.procedures.add(record);
    });

    void ensurePersistentStorage();

    return record;
  },

  async update(userId: string, id: string, changes: Partial<ProcedureInput>): Promise<void> {
    const existing = await db.procedures.get(id);
    if (existing === undefined || !isLive(existing, userId)) return;

    await writeWithOutbox('procedure', id, userId, async () => {
      await db.procedures.update(id, { ...changes, updatedAt: new Date(), syncedAt: null });
    });
  },

  /** Soft delete only. Nothing here removes a row. */
  async softDelete(userId: string, id: string): Promise<void> {
    const existing = await db.procedures.get(id);
    if (existing === undefined || !isLive(existing, userId)) return;

    await writeWithOutbox('procedure', id, userId, async () => {
      await db.procedures.update(id, {
        deletedAt: new Date(),
        updatedAt: new Date(),
        syncedAt: null,
      });
    });
  },

  async restore(userId: string, id: string): Promise<void> {
    const existing = await db.procedures.get(id);
    if (existing === undefined || existing.userId !== userId) return;

    await writeWithOutbox('procedure', id, userId, async () => {
      await db.procedures.update(id, {
        deletedAt: undefined,
        updatedAt: new Date(),
        syncedAt: null,
      });
    });
  },

  /**
   * Writes entries back from an export, in one transaction, each with its own
   * outbox item so the restored logbook reaches the server too.
   *
   * `source` becomes 'restore' so a later reader can tell these from entries
   * typed on this device. Ids are preserved — that is what makes a restore
   * idempotent, and what lets the caller skip anything already present rather
   * than creating a second copy of it.
   */
  async restoreFromExport(userId: string, records: readonly LocalProcedure[]): Promise<number> {
    if (records.length === 0) return 0;

    await db.transaction('rw', db.procedures, db.outbox, async () => {
      for (const record of records) {
        await db.procedures.put({
          ...record,
          userId,
          source: 'restore',
          updatedAt: record.updatedAt,
          syncedAt: null,
        });
        await db.outbox.add(outboxItem('procedure', record.id, userId));
      }
    });

    notifyLocalWrite();
    void ensurePersistentStorage();

    return records.length;
  },

  /**
   * Writes an imported batch in one transaction, each entry queued for sync.
   *
   * Every entry carries the same `importBatchId`, which is what makes "undo
   * this import" possible: a trainee who maps a column wrongly and imports 400
   * rows needs one button, not 400 deletions.
   *
   * Deduplicated by the caller against `dedupeKey`, so re-importing a
   * spreadsheet after adding a few lines to it brings in only the new lines.
   */
  async importBatch(
    userId: string,
    inputs: readonly ProcedureInput[],
    importBatchId: string,
  ): Promise<number> {
    if (inputs.length === 0) return 0;

    const now = new Date();

    const records: LocalProcedure[] = inputs.map((input) => ({
      ...input,
      id: uuidv7(),
      userId,
      source: 'import',
      importBatchId,
      createdAt: now,
      updatedAt: now,
      syncedAt: null,
    }));

    await db.transaction('rw', db.procedures, db.outbox, async () => {
      for (const record of records) {
        await db.procedures.add(record);
        await db.outbox.add(outboxItem('procedure', record.id, userId));
      }
    });

    notifyLocalWrite();
    void ensurePersistentStorage();

    return records.length;
  },

  /**
   * Undoes an import. Soft delete, like every other removal here — the rows
   * stay and sync as deletions, so undoing an import on one device undoes it
   * everywhere rather than leaving the entries alive on a laptop.
   */
  async undoImport(userId: string, importBatchId: string): Promise<number> {
    const all = await db.procedures.toArray();
    const batch = all.filter(
      (record) =>
        record.userId === userId &&
        record.importBatchId === importBatchId &&
        record.deletedAt === undefined,
    );

    if (batch.length === 0) return 0;

    const now = new Date();

    await db.transaction('rw', db.procedures, db.outbox, async () => {
      for (const record of batch) {
        await db.procedures.update(record.id, { deletedAt: now, updatedAt: now, syncedAt: null });
        await db.outbox.add(outboxItem('procedure', record.id, userId));
      }
    });

    notifyLocalWrite();
    return batch.length;
  },

  /** The import batches on this device, newest first, for the undo list. */
  async importBatches(
    userId: string,
  ): Promise<{ importBatchId: string; count: number; importedAt: Date }[]> {
    const all = await db.procedures.toArray();
    const batches = new Map<string, { count: number; importedAt: Date }>();

    for (const record of all) {
      const batchId = record.importBatchId;
      if (record.userId !== userId || batchId === undefined || record.deletedAt !== undefined) {
        continue;
      }

      const existing = batches.get(batchId);
      if (existing === undefined) batches.set(batchId, { count: 1, importedAt: record.createdAt });
      else existing.count += 1;
    }

    return [...batches.entries()]
      .map(([importBatchId, value]) => ({ importBatchId, ...value }))
      .sort((a, b) => b.importedAt.getTime() - a.importedAt.getTime());
  },

  async get(userId: string, id: string): Promise<LocalProcedure | undefined> {
    const record = await db.procedures.get(id);
    return record !== undefined && isLive(record, userId) ? record : undefined;
  },

  async list(userId: string): Promise<LocalProcedure[]> {
    const [lower, upper] = userRange(userId);
    const records = await db.procedures
      .where('[userId+occurredAt]')
      .between(lower, upper, true, true)
      .toArray();

    return records.filter((record) => isLive(record, userId)).sort(byOccurredAtDescending);
  },

  /** The procedures recorded inside one case, oldest first as they happened. */
  async listByCase(userId: string, caseId: string): Promise<LocalProcedure[]> {
    const records = await db.procedures.where('caseId').equals(caseId).toArray();

    return records
      .filter((record) => isLive(record, userId))
      .sort((a, b) => a.occurredAt.getTime() - b.occurredAt.getTime());
  },

  async listDeleted(userId: string): Promise<LocalProcedure[]> {
    const records = await db.procedures.toArray();

    return records
      .filter((record) => record.userId === userId && record.deletedAt !== undefined)
      .sort(byOccurredAtDescending);
  },

  /**
   * The keys of the five procedures this trainee logged most recently, most
   * recent first and each appearing once. The picker pins these above the
   * alphabetical list, which is most of why a routine arterial line is three
   * taps rather than a search.
   */
  async recentlyUsedKeys(userId: string): Promise<string[]> {
    const recent = await this.list(userId);
    const keys: string[] = [];

    for (const procedure of recent) {
      if (!keys.includes(procedure.procedureKey)) keys.push(procedure.procedureKey);
      if (keys.length === RECENT_PROCEDURE_COUNT) break;
    }

    return keys;
  },

  /**
   * An unsaved copy dated now — the "same procedure again, different patient"
   * path, which should be one tap from the entry list.
   *
   * `caseId` is deliberately not carried over: a duplicate is a new, standalone
   * procedure, not a second child of the original's case.
   */
  async duplicate(userId: string, id: string): Promise<ProcedureInput | undefined> {
    const source = await this.get(userId, id);
    if (source === undefined) return undefined;

    return {
      procedureKey: source.procedureKey,
      occurredAt: new Date(),
      ageBand: source.ageBand,
      capabilityLevel: source.capabilityLevel,
      ...(source.variant === undefined ? {} : { variant: source.variant }),
      ...(source.side === undefined ? {} : { side: source.side }),
      ...(source.ultrasoundGuided === undefined
        ? {}
        : { ultrasoundGuided: source.ultrasoundGuided }),
      ...(source.indication === undefined ? {} : { indication: source.indication }),
      ...(source.hillos === undefined ? {} : { hillos: [...source.hillos] }),
      // Attempts, success, complications and who was supervised describe one
      // specific attempt on one specific patient. Carrying them into a copy
      // would be a lie.
    };
  },
};
